/**
 * Helpers for the profile fields shared by specialists and consultants.
 */
import type { ProviderProfile } from '../types/providerProfile';

/** Week as shown in the UI (Saturday first). Values follow the backend: 0 = Monday … 6 = Sunday. */
export const WEEK_DAYS = [5, 6, 0, 1, 2, 3, 4] as const;

export const DEFAULT_WORKING_HOURS = { from: '09:00', to: '17:00' };

export const emptyProviderProfile = (): ProviderProfile => ({
  preferred_contact_method: '',
  preferred_contact_time: '',
  contact_notes: '',
  clinic_address: '',
  clinic_location_url: '',
  service_cities: [],
  service_areas: [],
  weekly_schedule: [],
});

export const providerProfileFromApi = (provider?: Partial<ProviderProfile> | null): ProviderProfile => ({
  preferred_contact_method: provider?.preferred_contact_method ?? '',
  preferred_contact_time: provider?.preferred_contact_time ?? '',
  contact_notes: provider?.contact_notes ?? '',
  clinic_address: provider?.clinic_address ?? '',
  clinic_location_url: provider?.clinic_location_url ?? '',
  service_cities: provider?.service_cities ?? [],
  service_areas: provider?.service_areas ?? [],
  weekly_schedule: provider?.weekly_schedule ?? [],
});

/** Trim, drop empties and case-insensitive duplicates (keeps the first spelling). */
export const cleanPlaces = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of values) {
    const name = raw.trim().replace(/\s+/g, ' ');
    const key = name.toLocaleLowerCase();
    if (name && !seen.has(key)) {
      seen.add(key);
      result.push(name);
    }
  }
  return result;
};

/**
 * Add the profile to a multipart body. Lists go as JSON strings; every field is always
 * sent so clearing a value in the form clears it on the server.
 */
export const appendProviderProfile = (body: FormData, profile: ProviderProfile): void => {
  body.append('preferred_contact_method', profile.preferred_contact_method);
  body.append('preferred_contact_time', profile.preferred_contact_time.trim());
  body.append('contact_notes', profile.contact_notes.trim());
  body.append('clinic_address', profile.clinic_address.trim());
  body.append('clinic_location_url', profile.clinic_location_url.trim());
  body.append('service_cities', JSON.stringify(cleanPlaces(profile.service_cities)));
  body.append('service_areas', JSON.stringify(cleanPlaces(profile.service_areas)));
  body.append('weekly_schedule', JSON.stringify(profile.weekly_schedule));
};

/** Client-side checks; returns field → i18n key for the first problem of each field. */
export const validateProviderProfile = (profile: ProviderProfile): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (profile.clinic_location_url.trim() && !/^https?:\/\/\S+$/i.test(profile.clinic_location_url.trim())) {
    errors.clinic_location_url = 'providers.errors.invalidUrl';
  }
  if (profile.weekly_schedule.some((entry) => !entry.from || !entry.to || entry.from >= entry.to)) {
    errors.weekly_schedule = 'providers.errors.invalidHours';
  }
  return errors;
};

/** First field message from an API validation error (`{ errors: { field: [message] } }`), if any. */
export const firstApiFieldError = (err: unknown): string | undefined => {
  const errors = (err as { data?: { errors?: Record<string, unknown> } })?.data?.errors;
  if (!errors || typeof errors !== 'object') return undefined;
  const first = Object.values(errors)[0];
  const message = Array.isArray(first) ? first[0] : first;
  return typeof message === 'string' ? message : undefined;
};
